import { useState } from 'react';
import { ShoppingCart, X, Minus, Plus } from 'lucide-react';
import { useCart } from '../../context/CartContext';

/**
 * Floating cart bar + expandable summary. Visible only when the cart has
 * items. Single-event scope (CartContext enforces this). Clicking CHECKOUT
 * delegates to the parent via `onCheckout()` — the page runs its existing
 * buyer-contact dialog then POSTs to /checkout/create-cart-order.
 */
export default function CartBar({ onCheckout, salesClosed, eventId }) {
  const { items, totals, updateQuantity, removeItem, eventId: cartEventId } = useCart();
  const [expanded, setExpanded] = useState(false);

  // Hide when cart is empty OR holds items from a different event (cart is
  // single-event scope — the other event's EventDetailPage renders its own).
  if (!items || items.length === 0) return null;
  if (eventId && cartEventId && cartEventId !== eventId) return null;

  const formatPrice = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  return (
    <>
      {/* Backdrop when expanded */}
      {expanded && (
        <div
          className="fixed inset-0 z-40 bg-black/60"
          onClick={() => setExpanded(false)}
        />
      )}

      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-[#050505] border-t border-[#B5FF00]/30 shadow-[0_-20px_40px_rgba(0,0,0,0.6)]"
        data-testid="cart-bar"
      >
        {expanded && (
          <div
            className="max-w-5xl mx-auto px-4 sm:px-6 py-4 border-b border-zinc-900 max-h-[50vh] overflow-y-auto"
            data-testid="cart-summary"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-mono text-xs text-zinc-400 tracking-widest">YOUR CART · {items.length} LINE{items.length !== 1 ? 'S' : ''}</h3>
              <button
                onClick={() => setExpanded(false)}
                className="text-zinc-400 hover:text-white"
                data-testid="cart-collapse-btn"
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it) => {
                const key = `${it.tier_id}::${it.date_id || ''}`;
                const lineTotal = Number(it.unit_price || 0) * Number(it.quantity || 0);
                return (
                  <div
                    key={key}
                    className="flex items-center justify-between gap-3 bg-[#0A0A0A] border border-zinc-800 px-3 py-2"
                    data-testid={`cart-line-${it.tier_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-['Josefin_Sans'] truncate">
                        {it.tier_name}
                        {it.date_label && (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] font-mono tracking-wider bg-[#B5FF00]/10 text-[#B5FF00] border border-[#B5FF00]/30 align-middle">
                            {String(it.date_label).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="font-mono text-[11px] text-zinc-500">
                        {Number(it.unit_price || 0) <= 0 ? 'FREE' : `${formatPrice(it.unit_price)} × ${it.quantity}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => updateQuantity(key, Math.max(0, it.quantity - 1))}
                        className="w-7 h-7 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white"
                        data-testid={`cart-qty-dec-${it.tier_id}`}
                      >
                        <Minus size={12} />
                      </button>
                      <span className="font-mono text-sm text-white w-6 text-center" data-testid={`cart-qty-${it.tier_id}`}>
                        {it.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(key, it.quantity + 1)}
                        className="w-7 h-7 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white"
                        data-testid={`cart-qty-inc-${it.tier_id}`}
                      >
                        <Plus size={12} />
                      </button>
                      <span className="font-mono text-sm text-[#B5FF00] w-16 text-right">
                        {Number(it.unit_price || 0) <= 0 ? 'FREE' : formatPrice(lineTotal)}
                      </span>
                      <button
                        onClick={() => removeItem(key)}
                        className="text-zinc-500 hover:text-red-400 ml-1"
                        data-testid={`cart-remove-${it.tier_id}`}
                        title="Remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-3 flex-1 text-left"
            data-testid="cart-expand-btn"
          >
            <div className="relative">
              <ShoppingCart size={22} className="text-[#B5FF00]" />
              <span className="absolute -top-1 -right-1 bg-[#B5FF00] text-black text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {totals.count}
              </span>
            </div>
            <div className="min-w-0">
              <div className="font-mono text-[10px] text-zinc-500 tracking-widest">
                {totals.allFree ? 'FREE RSVP' : 'SUBTOTAL · FEES ADDED AT CHECKOUT'}
              </div>
              <div className="font-['Josefin_Sans'] text-xl text-white">
                {totals.allFree ? 'FREE' : formatPrice(totals.subtotal)}
              </div>
            </div>
          </button>
          <button
            onClick={onCheckout}
            disabled={salesClosed}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-6 py-2.5"
            data-testid="cart-checkout-btn"
          >
            {salesClosed ? 'SALES CLOSED' : (totals.allFree ? 'CLAIM FREE' : 'CHECKOUT')}
          </button>
        </div>
      </div>
    </>
  );
}
